<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import type { Product, PublishingCertificate, SigningRoot, SigningRootStatus } from '@/types'
import { router, useForm } from '@inertiajs/vue3'
import { useToast } from '@nuxt/ui/composables'
import { useClipboard } from '@vueuse/core'
import { computed, ref } from 'vue'
import { formatDate } from '@/lib/format'
import { firstFormError } from '@/lib/forms'
import {
  adminProductPublishingCertificatesPath,
  adminProductSigningRootsPath,
  adminPublishingCertificatePath,
  adminSigningRootPath,
} from '@/routes'

type RootSlot = 'active' | 'standby'

const props = defineProps<{
  product: Pick<Product, 'slug' | 'name' | 'signing_roots' | 'publishing_certificates'>
}>()

const toast = useToast()
const { copy } = useClipboard()

function copyValue(value: string, label: string) {
  copy(value)
  toast.add({ title: `${label} copied`, icon: 'i-lucide-clipboard-check', color: 'success' })
}

const roots = computed(() => props.product.signing_roots ?? [])
const certificates = computed(() => props.product.publishing_certificates ?? [])

// The product holds exactly one active and one standby root; the backend
// enforces it, so each slot resolves to at most one root.
const rootSlots = computed(() => [
  { slot: 'active' as RootSlot, title: 'Active root', root: roots.value.find(root => root.status === 'active') ?? null },
  { slot: 'standby' as RootSlot, title: 'Standby root', root: roots.value.find(root => root.status === 'standby') ?? null },
])
const retiredRoots = computed(() => roots.value.filter(root => root.status === 'retired'))

function rootGenerateCommand(slot: RootSlot) {
  return `bin/rails signing:root:generate PRODUCT=${props.product.slug} STATUS=${slot}`
}

const certificateIssueCommand = computed(() => [
  'bin/rails signing:certificate:issue \\',
  `  PRODUCT=${props.product.slug} \\`,
  '  ROOT_KEY_FILE=<path-to-root-private-key.pem>',
].join('\n'))

const githubSecret = (name: string) => `\${{ secrets.${name} }}`
const githubActionSnippet = [
  '- name: Upload to ZLP',
  '  uses: Zerya-Dev/licensing-platform/.github/actions/release-publish@master',
  '  with:',
  '    artifacts: |',
  '      binary=dist/app.zip',
  `    signing-key: ${githubSecret('ZLP_SIGNING_KEY')}`,
  `    signing-key-id: ${githubSecret('ZLP_SIGNING_KEY_ID')}`,
].join('\n')

// --- Add root -------------------------------------------------------------
const rootOpen = ref(false)
const rootSlot = ref<RootSlot>('standby')
const rootForm = useForm({ label: '', public_key: '', status: 'standby' as RootSlot })

function openAddRoot(slot: RootSlot) {
  rootSlot.value = slot
  rootForm.defaults({ label: '', public_key: '', status: slot })
  rootForm.reset()
  rootForm.clearErrors()
  rootOpen.value = true
}

function submitRoot() {
  rootForm.post(adminProductSigningRootsPath(props.product.slug), {
    preserveScroll: true,
    onSuccess: () => (rootOpen.value = false),
  })
}

const retireTarget = ref<SigningRoot | null>(null)
const retiring = ref(false)

function setRootStatus(root: SigningRoot, status: SigningRootStatus) {
  router.patch(adminSigningRootPath(root.id), { status }, { preserveScroll: true })
}

// Per-root transitions. Active/standby moves swap the two slots server-side, so
// they are always offered; restoring a retired root is disabled while its target
// slot is taken.
function rootMenu(root: SigningRoot): DropdownMenuItem[][] {
  if (root.status === 'active') {
    return [
      [{ label: rootSlots.value[1].root !== null ? 'Swap with standby' : 'Move to standby', icon: 'i-lucide-arrow-down-up', onSelect: () => setRootStatus(root, 'standby') }],
      [{ label: 'Retire', icon: 'i-lucide-archive', color: 'error', onSelect: () => (retireTarget.value = root) }],
    ]
  }
  if (root.status === 'standby') {
    return [
      [{ label: rootSlots.value[0].root !== null ? 'Swap with active' : 'Promote to active', icon: 'i-lucide-arrow-up', onSelect: () => setRootStatus(root, 'active') }],
      [{ label: 'Retire', icon: 'i-lucide-archive', color: 'error', onSelect: () => (retireTarget.value = root) }],
    ]
  }
  return [[
    { label: 'Restore as active', icon: 'i-lucide-arrow-up', disabled: rootSlots.value[0].root !== null, onSelect: () => setRootStatus(root, 'active') },
    { label: 'Restore as standby', icon: 'i-lucide-key-round', disabled: rootSlots.value[1].root !== null, onSelect: () => setRootStatus(root, 'standby') },
  ]]
}

function retireRoot() {
  if (!retireTarget.value)
    return

  router.delete(adminSigningRootPath(retireTarget.value.id), {
    preserveScroll: true,
    onStart: () => (retiring.value = true),
    onFinish: () => {
      retiring.value = false
      retireTarget.value = null
    },
  })
}

// --- Publishing certificates ---------------------------------------------
const certificateOpen = ref(false)
const certificateForm = useForm({ certificate_json: '' })
const certificateImportError = computed(() => {
  const errors = certificateForm.errors as Record<string, string[] | undefined>
  return firstFormError(errors.certificate_json)
    || firstFormError(errors.key_id)
    || firstFormError(errors.signing_public_key)
    || firstFormError(errors.root_signature)
    || firstFormError(errors.not_before)
    || firstFormError(errors.not_after)
})

function openImportCertificate() {
  certificateForm.reset()
  certificateForm.clearErrors()
  certificateOpen.value = true
}

function submitCertificate() {
  certificateForm.post(adminProductPublishingCertificatesPath(props.product.slug), {
    preserveScroll: true,
    onSuccess: () => (certificateOpen.value = false),
  })
}

const revokeTarget = ref<PublishingCertificate | null>(null)
const revoking = ref(false)

function revokeCertificate() {
  if (!revokeTarget.value)
    return

  router.delete(adminPublishingCertificatePath(revokeTarget.value.id), {
    preserveScroll: true,
    onStart: () => (revoking.value = true),
    onFinish: () => {
      revoking.value = false
      revokeTarget.value = null
    },
  })
}

function certificateStatus(certificate: PublishingCertificate): { color: 'success' | 'warning' | 'error' | 'neutral', label: string } {
  if (certificate.revoked)
    return { color: 'neutral', label: 'Revoked' }
  if (!certificate.root_signature_valid)
    return { color: 'error', label: 'Untrusted chain' }
  if (!certificate.active)
    return { color: 'warning', label: 'Out of window' }
  return { color: 'success', label: 'Active' }
}
</script>

<template>
  <div class="flex flex-col gap-10">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-highlighted font-semibold">
          Release signing
        </h2>
        <p class="text-muted text-sm">
          The trust chain that lets clients verify {{ product.name }} releases.
        </p>
      </div>

      <UPopover :content="{ align: 'end' }">
        <UButton label="How it works" icon="i-lucide-info" color="neutral" variant="subtle" size="sm" />
        <template #content>
          <div class="text-muted max-w-sm space-y-3 p-4 text-sm">
            <p>
              The product binary pins two <span class="text-highlighted">root public keys</span> (active and standby) by hand — that is the trust anchor.
            </p>
            <p>
              A root's offline private key signs a <span class="text-highlighted">publishing certificate</span> that delegates signing to a GitHub Actions key. GitHub Actions then signs each release descriptor with that key.
            </p>
            <p>
              ZLP stores the public roots only to verify and display the chain. Root private keys never touch ZLP or GitHub.
            </p>
          </div>
        </template>
      </UPopover>
    </div>

    <!-- Roots -->
    <section class="flex flex-col gap-4">
      <div>
        <h3 class="text-highlighted text-sm font-medium">
          Trusted roots
        </h3>
        <p class="text-muted text-sm">
          Two slots, pinned in the client by hand. Swap or retire them from each root's menu to rotate trust.
        </p>
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        <div
          v-for="{ slot, title, root } in rootSlots"
          :key="slot"
          class="ring-default flex min-h-32 flex-col rounded-lg p-4 ring"
        >
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center gap-2">
              <UIcon
                :name="slot === 'active' ? 'i-lucide-shield-check' : 'i-lucide-shield'"
                class="size-4 shrink-0"
                :class="root ? (slot === 'active' ? 'text-success' : 'text-muted') : 'text-dimmed'"
              />
              <span class="text-highlighted text-sm font-medium">{{ title }}</span>
            </div>
            <UDropdownMenu v-if="root" :items="rootMenu(root)">
              <UButton icon="i-lucide-ellipsis-vertical" color="neutral" variant="ghost" size="xs" square aria-label="Root actions" />
            </UDropdownMenu>
          </div>

          <div v-if="root" class="mt-3 flex flex-col gap-1">
            <span v-if="root.label" class="text-muted text-xs">{{ root.label }}</span>
            <div class="flex items-center gap-2">
              <code class="text-highlighted truncate font-mono text-xs">{{ root.fingerprint ?? 'Invalid key' }}</code>
              <UButton
                icon="i-lucide-copy"
                color="neutral"
                variant="ghost"
                size="xs"
                square
                aria-label="Copy public key"
                @click="copyValue(root.public_key, 'Public key')"
              />
            </div>
          </div>

          <div v-else class="mt-3 flex flex-1 flex-col items-start justify-between gap-4">
            <p class="text-dimmed text-sm">
              No {{ slot }} root pinned.
            </p>
            <UButton
              :label="`Add ${slot} root`"
              icon="i-lucide-plus"
              variant="subtle"
              size="sm"
              @click="openAddRoot(slot)"
            />
          </div>
        </div>
      </div>

      <UCollapsible v-if="retiredRoots.length" class="text-sm">
        <UButton
          :label="`Retired roots (${retiredRoots.length})`"
          icon="i-lucide-history"
          color="neutral"
          variant="link"
          size="sm"
          trailing-icon="i-lucide-chevron-down"
          class="-ml-2"
        />
        <template #content>
          <ul class="border-default mt-2 flex flex-col gap-2 rounded-lg border p-3">
            <li v-for="root in retiredRoots" :key="root.id" class="flex items-center justify-between gap-2">
              <code class="text-muted truncate font-mono text-xs">{{ root.fingerprint ?? 'Invalid key' }}</code>
              <div class="flex items-center gap-2">
                <span v-if="root.label" class="text-dimmed text-xs">{{ root.label }}</span>
                <UDropdownMenu :items="rootMenu(root)">
                  <UButton icon="i-lucide-ellipsis-vertical" color="neutral" variant="ghost" size="xs" square aria-label="Root actions" />
                </UDropdownMenu>
              </div>
            </li>
          </ul>
        </template>
      </UCollapsible>
    </section>

    <!-- Publishing certificates -->
    <section class="flex flex-col gap-4">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 class="text-highlighted text-sm font-medium">
            Publishing certificates
          </h3>
          <p class="text-muted text-sm">
            Root-signed delegations that let a GitHub Actions key sign releases.
          </p>
        </div>
        <UButton label="Import certificate" icon="i-lucide-plus" size="sm" @click="openImportCertificate" />
      </div>

      <div
        v-if="certificates.length === 0"
        class="ring-default text-muted rounded-lg p-10 text-center text-sm ring"
      >
        No publishing certificates yet. Issue one with
        <code class="font-mono">signing:certificate:issue</code> and import it here.
      </div>

      <div
        v-for="certificate in certificates"
        :key="certificate.id"
        class="ring-default flex flex-wrap items-start justify-between gap-4 rounded-lg p-4 ring"
      >
        <div class="min-w-0">
          <div class="mb-1 flex flex-wrap items-center gap-2">
            <code class="text-highlighted font-mono text-sm font-medium">{{ certificate.key_id }}</code>
            <UBadge v-bind="certificateStatus(certificate)" variant="subtle" size="sm" />
          </div>
          <div class="text-muted font-mono text-xs">
            {{ certificate.signing_public_key_fingerprint ?? 'Invalid signing key' }}
          </div>
          <div class="text-dimmed mt-1 text-xs">
            {{ formatDate(certificate.not_before) }} → {{ formatDate(certificate.not_after) }}
          </div>
        </div>

        <UButton
          label="Revoke"
          icon="i-lucide-ban"
          color="error"
          variant="ghost"
          size="xs"
          :disabled="certificate.revoked"
          @click="revokeTarget = certificate"
        />
      </div>
    </section>
  </div>

  <!-- Add root modal -->
  <UModal v-model:open="rootOpen" :title="`Add ${rootSlot} root`">
    <template #body>
      <form class="flex flex-col gap-5" @submit.prevent="submitRoot">
        <p class="text-muted text-sm">
          Generate the key pair on an offline machine, then paste its public key here.
        </p>

        <pre class="bg-muted/40 text-muted overflow-auto rounded-lg p-3 text-xs">{{ rootGenerateCommand(rootSlot) }}</pre>

        <UFormField label="Label" name="label" :error="firstFormError(rootForm.errors.label)">
          <UInput v-model="rootForm.label" :placeholder="`${rootSlot}-${new Date().getFullYear()}`" class="w-full font-mono" />
        </UFormField>

        <UFormField
          label="Public key"
          name="public_key"
          description="Base64 public key from the command output — not the PEM private key."
          :error="firstFormError(rootForm.errors.public_key)"
          required
        >
          <UTextarea v-model="rootForm.public_key" :rows="3" class="w-full font-mono" />
        </UFormField>
      </form>
    </template>

    <template #footer>
      <div class="flex w-full justify-end gap-3">
        <UButton label="Cancel" color="neutral" variant="ghost" @click="rootOpen = false" />
        <UButton
          :label="`Add ${rootSlot} root`"
          icon="i-lucide-key-round"
          :loading="rootForm.processing"
          :disabled="!rootForm.public_key"
          @click="submitRoot"
        />
      </div>
    </template>
  </UModal>

  <!-- Import certificate modal -->
  <UModal v-model:open="certificateOpen" title="Import publishing certificate" size="xl">
    <template #body>
      <form class="flex flex-col gap-5" @submit.prevent="submitCertificate">
        <p class="text-muted text-sm">
          Run this on the offline machine with the root private key, then paste the printed JSON below.
        </p>

        <pre class="bg-muted/40 text-muted overflow-auto rounded-lg p-3 text-xs">{{ certificateIssueCommand }}</pre>

        <UFormField
          label="Certificate JSON"
          name="certificate_json"
          :error="certificateImportError"
          required
        >
          <UTextarea v-model="certificateForm.certificate_json" :rows="10" class="w-full font-mono" />
        </UFormField>

        <UCollapsible class="text-sm">
          <UButton
            label="GitHub Actions setup"
            icon="i-lucide-github"
            color="neutral"
            variant="link"
            size="sm"
            trailing-icon="i-lucide-chevron-down"
            class="-ml-2"
          />
          <template #content>
            <p class="text-muted mt-2 text-sm">
              Store the printed PEM as <code class="font-mono">ZLP_SIGNING_KEY</code> and the key id as
              <code class="font-mono">ZLP_SIGNING_KEY_ID</code>, then pass them to the action:
            </p>
            <pre class="bg-muted/40 text-muted mt-2 overflow-auto rounded-lg p-3 text-xs">{{ githubActionSnippet }}</pre>
          </template>
        </UCollapsible>
      </form>
    </template>

    <template #footer>
      <div class="flex w-full justify-end gap-3">
        <UButton label="Cancel" color="neutral" variant="ghost" @click="certificateOpen = false" />
        <UButton
          label="Import certificate"
          icon="i-lucide-badge-check"
          :loading="certificateForm.processing"
          :disabled="!certificateForm.certificate_json"
          @click="submitCertificate"
        />
      </div>
    </template>
  </UModal>

  <!-- Retire root confirm -->
  <UModal
    :open="retireTarget !== null"
    title="Retire signing root"
    :description="retireTarget ? `Retire this ${retireTarget.status} root? It will no longer verify publishing certificates. Clients keep trusting it until you ship a build without it pinned.` : ''"
    @update:open="(value: boolean) => { if (!value) retireTarget = null }"
  >
    <template #footer>
      <div class="flex w-full justify-end gap-3">
        <UButton label="Cancel" color="neutral" variant="ghost" @click="retireTarget = null" />
        <UButton label="Retire" color="neutral" icon="i-lucide-archive" :loading="retiring" @click="retireRoot" />
      </div>
    </template>
  </UModal>

  <!-- Revoke certificate confirm -->
  <UModal
    :open="revokeTarget !== null"
    title="Revoke publishing certificate"
    :description="revokeTarget ? `Revoke ${revokeTarget.key_id}? Releases signed with this key will stop verifying as active.` : ''"
    @update:open="(value: boolean) => { if (!value) revokeTarget = null }"
  >
    <template #footer>
      <div class="flex w-full justify-end gap-3">
        <UButton label="Cancel" color="neutral" variant="ghost" @click="revokeTarget = null" />
        <UButton label="Revoke" color="error" icon="i-lucide-ban" :loading="revoking" @click="revokeCertificate" />
      </div>
    </template>
  </UModal>
</template>
